import { Http } from '@capacitor-community/http'
import { computed, ComputedRef, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { Token_JWT } from '../types/components/data-rendering'
import { AuthenticationModel } from '../models/Authentication'
import { Capacitor } from '@capacitor/core'
import { useStore } from '../store'
import { useAPIURLs } from '../composables/useAPIURLs'
import { clearQueue, loadQueue } from '../boot/loader/loader'

export enum LoginState {
  FETCHING = 'fetching',
  ERROR = 'error',
  DEFAULT = 'default',
}
type UseAuthenticateSAML = {
  /** sets saml id (e.g. ONELOGIN_asdf789qwer987asdf789af987asdf789qwer897). A watcher
   *  will then grab a JWT from the server.
   */
  setSamlID: (data: string) => void
  /** Returns a JWT*/
  getJWT: ComputedRef<Token_JWT>
  /** Sets JWT */
  setJWT: (token: Token_JWT) => Promise<void>
  /** Start saml request and redirect to login */
  login: (username: string | undefined) => Promise<void>
  /** Removes JWT from device */
  logout: () => Promise<void>
  /** Sets logged in flag to false for rep */
  softLogout: () => Promise<void>
  /**Creates a JWT from switchTo */
  createJWT: (data: {
    suid: string //staffing users id
    uuid: string
    token: string //token
  }) => Promise<void>
  /**Gets state of fetching */
  getLoginState: ComputedRef<LoginState>
  /**gets flash messages from fetching jwt */
  getFlashMessage: ComputedRef<string | undefined>
  resetStoreState: () => void
}

//format ONELOGIN_asdf789qwer987asdf789af987asdf789qwer897
const samlID = ref('')
/**JWT token */
const JWT_Token = ref()
const loginState = ref<LoginState>(LoginState.DEFAULT)
const flashMessage = ref<string | undefined>()
export const useAuthenticateSAML = defineStore('authenticateSAML', (): UseAuthenticateSAML => {
  //setup
  const { writeJWT, removeJWT } = AuthenticationModel()
  const setSamlID = (data: string) => {
    samlID.value = data
  }
  const { API } = useAPIURLs()

  const fetchJWT = async () => {
    if (samlID.value === '') {
      return void 0
    }
    loginState.value = LoginState.FETCHING
    flashMessage.value = 'loggingIn'
    await Http.post({
      url: API.value.loginSaml,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        saml_request_id: samlID.value,
        browser_request: true,
      },
    })
      .catch((error) => {
        console.log(`Error syncing JWT: ${error}`)
        loginState.value = LoginState.ERROR
        flashMessage.value = 'connection'
      })
      .then(async (res) => {
        if (res && res.data.error) {
          switch (res.data.error) {
            case 'SAML request has expired':
              loginState.value = LoginState.ERROR
              flashMessage.value = 'expired'
              break
            default:
              loginState.value = LoginState.ERROR
              flashMessage.value = 'expired'
              break
          }
        }
        if (typeof res === 'undefined' || res.data.error) {
          samlID.value = ''
        } else if (res) {
          console.log(res)
          //compare against expired token's user id, and load data
          await compareTokens(res.data.staffing_users_id)
          await setJWT(res.data as unknown as Token_JWT)
          //reset these values
          samlID.value = ''
          localStorage.removeItem('saml_request')
          loginState.value = LoginState.DEFAULT
          flashMessage.value = undefined
        }
      })
  }

  const compareTokens = async (staffing_users_id: number) => {
    const oldToken = await AuthenticationModel().loadJWT()
    console.log({ oldToken })
    if (oldToken && !oldToken.active) {
      if (staffing_users_id == oldToken.staffing_users_id) {
        for (const loadItem of loadQueue) {
          await loadItem()
        }
      } else {
        for (const clearItem of clearQueue) {
          await clearItem()
        }
      }
    }
  }

  watch(samlID, async () => {
    if (samlID.value !== '') {
      await fetchJWT()
    }
  })

  const store = useStore()
  const setJWT = async (token: Token_JWT) => {
    await writeJWT(token).catch((error) => {
      console.log(error)
    })
    store.user.isLoggedIn = true
    JWT_Token.value = token
  }

  const getJWT = computed(() => {
    return JWT_Token.value
  })

  /*
   * Login logic
   */
  const login = async (username: string | undefined) => {
    await Http.post({
      //todo: change URL
      url: API.value.samlRequest,
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        browser_request: 'true',
        username: username,
      },
    })
      .then(async (res) => {
        const response = res

        if (Capacitor.isNativePlatform()) {
          // await Browser.open({ url: response.data.url_with_redirect })
          //@ts-expect-error its ok
          const inAppBrowser = cordova.InAppBrowser.open(
            response.data.url_with_redirect,
            '_blank',
            'location=yes,hideurlbar=yes,hidenavigationbuttons=yes,toolbarcolor=#ffffff,toolbartranslucent=yes'
          )
          inAppBrowser.addEventListener('loadstop', function () {
            inAppBrowser.executeScript(
              { code: 'window.location.href' },
              function (browserLocation: string) {
                if (String(browserLocation).includes('index.html#')) {
                  //we can close and log rep in
                  setSamlID(response.data.saml_request.request_id)
                  inAppBrowser.close()
                }
              }
            )
          })
        } else {
          localStorage.setItem('saml_request', JSON.stringify(res.data))
          window.location.href = response.data.url_with_redirect
        }
      })
      .catch((error) => console.log(error))
  }

  const logout = async () => {
    const token = { ...getJWT.value }
    void Http.post({
      url: API.value.logout,
      headers: {
        Authorization: `Bearer ${token.token}`,
        'Content-Type': 'application/json',
      },
      data: {
        token: token.token,
      },
    })
    JWT_Token.value = undefined
    await removeJWT().catch((error) => console.log(error))
  }
  const softLogout = async () => {
    const token = { ...getJWT.value }
    if (token?.token) {
      void Http.post({
        url: API.value.logout,
        headers: {
          Authorization: `Bearer ${token.token}`,
          'Content-Type': 'application/json',
        },
        data: {
          token: token.token,
        },
      })
    }

    JWT_Token.value = undefined
    await AuthenticationModel().inactivateJWT()
  }

  const createJWT = async (data: {
    suid: string //staffing users id
    token: string //token
    uuid: string //uuid for token
  }) => {
    await setJWT({
      staffing_users_id: data.suid,
      token: data.token,
      token_uuid: data.uuid,
    })
  }

  const getLoginState = computed(() => loginState.value)
  const getFlashMessage = computed(() => flashMessage.value)

  const resetStoreState = () => {
    samlID.value = ''
    // JWT_Token.value = undefined
  }

  return {
    setSamlID,
    getJWT,
    setJWT,
    createJWT,
    login,
    logout,
    softLogout,
    resetStoreState,
    getLoginState,
    getFlashMessage,
  }
})
